import { Plus, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useActionsBareme, useBaremeAdmin, useBaremesAdmin } from '@/api/hooks';
import { LIBELLE_STATUT_BAREME, type BaremeContenu, type StatutBareme } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ConfirmationDialog, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr } from '@/lib/utils';
import { OngletsAdministration } from './Onglets';

const COULEUR: Record<StatutBareme, string> = { EN_PREPARATION: 'bg-amber-100 text-amber-900', EN_VIGUEUR: 'bg-emerald-100 text-emerald-800', ARCHIVE: 'bg-neutral-200 text-neutral-700' };
const COLONNES_PERSONNEL: Array<[keyof BaremeContenu['personnel'][string]['int'], string]> = [
  ['pd', 'Perdiem'],
  ['lg', 'Logement'],
  ['rp', 'Représentation'],
  ['pr', 'Protocole'],
  ['tr', 'Transport'],
];

/** Cellule numérique : affichage simple, ou saisie quand le barème est en préparation. */
function Cellule({ valeur, edition, onChange }: { valeur: number; edition: boolean; onChange: (n: number) => void }) {
  return edition ? <Input type="number" min={0} step="1" className="num h-8 w-24 text-right" value={valeur} onChange={(e) => onChange(Number(e.target.value) || 0)} /> : <span className="num">{valeur.toLocaleString('fr-FR')}</span>;
}

export function Bareme() {
  const session = useSession();
  const admin = session?.utilisateur.profils.includes('ADMINISTRATEUR') ?? false;
  const versions = useBaremesAdmin();
  const [version, setVersion] = useState<string | undefined>(undefined);
  const choisie = version ?? versions.data?.find((v) => v.statut === 'EN_VIGUEUR')?.version ?? versions.data?.[0]?.version;
  const detail = useBaremeAdmin(choisie);
  const actions = useActionsBareme();
  const [contenu, setContenu] = useState<BaremeContenu | null>(null);
  const [creation, setCreation] = useState(false);
  const [nouvelle, setNouvelle] = useState({ version: `${new Date().getFullYear() + 1}-1`, libelle: '', source: '', debut: `${new Date().getFullYear() + 1}-01-01` });
  const [vigueur, setVigueur] = useState(false);

  useEffect(() => {
    if (detail.data) setContenu(structuredClone(detail.data.contenu));
  }, [detail.data]);

  const edition = Boolean(admin && detail.data?.statut === 'EN_PREPARATION');
  const b = contenu;
  const majPersonnel = (cat: string, zone: 'int' | 'ext', champ: string, n: number) => b && setContenu({ ...b, personnel: { ...b.personnel, [cat]: { ...b.personnel[cat]!, [zone]: { ...b.personnel[cat]![zone], [champ]: n } } } });
  const majTable = <K extends 'interprovincial' | 'intraprovincial'>(table: K, cat: string, champ: string, n: number) => b && setContenu({ ...b, [table]: { ...b[table], [cat]: { ...(b[table] as Record<string, Record<string, number>>)[cat], [champ]: n } } });

  const enregistrer = async () => {
    if (!choisie || !b) return;
    try {
      await actions.modifier.mutateAsync({ version: choisie, corps: { contenu: b } });
      toast.success('Barème enregistré.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  const creer = async () => {
    try {
      const r = await actions.creer.mutateAsync({ version: nouvelle.version.trim(), libelle: nouvelle.libelle.trim(), source: nouvelle.source.trim() || undefined, debut: nouvelle.debut, depuis: choisie });
      toast.success(`Version ${r.version} préparée à partir de ${choisie}.`);
      setCreation(false);
      setVersion(r.version);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Création impossible.');
    }
  };
  const mettreEnVigueur = async () => {
    if (!choisie) return;
    try {
      await actions.mettreEnVigueur.mutateAsync(choisie);
      toast.success(`Version ${choisie} en vigueur ; la version précédente est archivée.`);
      setVigueur(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Mise en vigueur impossible.');
    }
  };

  return (
    <div>
      <PageHeader
        titre="Administration"
        sousTitre="Barème des frais de mission : une version complète par période ; les documents déjà soumis gardent la leur."
        actions={
          admin && (
            <>
              {edition && (
                <>
                  <Button variant="outline" onClick={enregistrer} disabled={actions.modifier.isPending}>
                    <Save className="size-4" /> Enregistrer
                  </Button>
                  <Button onClick={() => setVigueur(true)}>Mettre en vigueur</Button>
                </>
              )}
              <Button variant={edition ? 'outline' : 'default'} onClick={() => { setNouvelle({ ...nouvelle, libelle: detail.data ? `${detail.data.libelle} (révision)` : '' }); setCreation(true); }}>
                <Plus className="size-4" /> Nouvelle version
              </Button>
            </>
          )
        }
      />
      <OngletsAdministration />
      {versions.isLoading ? (
        <Chargement />
      ) : versions.error ? (
        <ErreurBloc erreur={versions.error} />
      ) : !versions.data?.length ? (
        <Vide message="Aucune version du barème." />
      ) : (
        <div className="space-y-6">
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="hidden sm:table-cell">Source</TableHead>
                  <TableHead>Période</TableHead>
                  <TableHead>État</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.data.map((v) => (
                  <TableRow key={v.version} className={cn('cursor-pointer', v.version === choisie && 'bg-muted/60')} onClick={() => setVersion(v.version)}>
                    <TableCell className="font-medium">{v.version}</TableCell>
                    <TableCell>{v.libelle}</TableCell>
                    <TableCell className="hidden sm:table-cell">{v.source}</TableCell>
                    <TableCell>
                      {dateFr(v.debut)} → {v.fin ? dateFr(v.fin) : '…'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn('border-transparent', COULEUR[v.statut])}>
                        {LIBELLE_STATUT_BAREME[v.statut]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {detail.isLoading || !b ? (
            <Chargement />
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>
                    Barème du personnel · version {b.version}
                    {edition && <span className="ml-2 text-sm font-normal text-amber-700">en préparation : les montants sont modifiables</span>}
                  </CardTitle>
                  <CardDescription>Montants par jour en USD (logement par nuitée) ; intérieur du pays et étranger.</CardDescription>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead rowSpan={2}>Catégorie</TableHead>
                        <TableHead colSpan={5} className="text-center">Intérieur</TableHead>
                        <TableHead colSpan={5} className="text-center">Étranger</TableHead>
                      </TableRow>
                      <TableRow>
                        {COLONNES_PERSONNEL.map(([k, l]) => (
                          <TableHead key={`i-${k}`} className="text-right">{l}</TableHead>
                        ))}
                        {COLONNES_PERSONNEL.map(([k, l]) => (
                          <TableHead key={`e-${k}`} className="text-right">{l}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(b.personnel).map(([cat, v]) => (
                        <TableRow key={cat}>
                          <TableCell>
                            <span className="font-medium">{cat}</span> <span className="text-xs text-muted-foreground">{v.libelle}</span>
                          </TableCell>
                          {COLONNES_PERSONNEL.map(([k]) => (
                            <TableCell key={`i-${k}`} className="text-right"><Cellule valeur={v.int[k]} edition={edition} onChange={(n) => majPersonnel(cat, 'int', k, n)} /></TableCell>
                          ))}
                          {COLONNES_PERSONNEL.map(([k]) => (
                            <TableCell key={`e-${k}`} className="text-right"><Cellule valeur={v.ext[k]} edition={edition} onChange={(n) => majPersonnel(cat, 'ext', k, n)} /></TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Coordinations · mission interprovinciale</CardTitle>
                    <CardDescription>Hébergement par nuitée, perdiem et transport par jour (USD).</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Catégorie</TableHead>
                          <TableHead className="text-right">Hébergement</TableHead>
                          <TableHead className="text-right">Perdiem</TableHead>
                          <TableHead className="text-right">Transport</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(b.interprovincial).map(([cat, v]) => (
                          <TableRow key={cat}>
                            <TableCell className="font-medium">{cat}</TableCell>
                            {(['hb', 'pd', 'tr'] as const).map((k) => (
                              <TableCell key={k} className="text-right"><Cellule valeur={v[k]} edition={edition} onChange={(n) => majTable('interprovincial', cat, k, n)} /></TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Coordinations · mission intraprovinciale</CardTitle>
                    <CardDescription>Perdiem et transport par jour ; hébergement selon la ville. Kinshasa (siège) : perdiem seul.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Catégorie</TableHead>
                          <TableHead className="text-right">Perdiem</TableHead>
                          <TableHead className="text-right">Transport</TableHead>
                          <TableHead className="text-right">Kinshasa</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(b.intraprovincial).map(([cat, v]) => (
                          <TableRow key={cat}>
                            <TableCell className="font-medium">{cat}</TableCell>
                            {(['pd', 'tr'] as const).map((k) => (
                              <TableCell key={k} className="text-right"><Cellule valeur={v[k]} edition={edition} onChange={(n) => majTable('intraprovincial', cat, k, n)} /></TableCell>
                            ))}
                            <TableCell className="text-right"><Cellule valeur={b.kinshasaIntra[cat] ?? 0} edition={edition} onChange={(n) => setContenu({ ...b, kinshasaIntra: { ...b.kinshasaIntra, [cat]: n } })} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Villes · hébergement par nuitée</CardTitle>
                  </CardHeader>
                  <CardContent className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Coordination</TableHead>
                          <TableHead>Province</TableHead>
                          <TableHead>Ville</TableHead>
                          <TableHead className="text-right">Hébergement</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {b.villes.map((v, i) => (
                          <TableRow key={`${v.province}-${v.ville}`}>
                            <TableCell>{v.coordination}</TableCell>
                            <TableCell>{v.province}</TableCell>
                            <TableCell>{v.ville}</TableCell>
                            <TableCell className="text-right"><Cellule valeur={v.hebergement} edition={edition} onChange={(n) => setContenu({ ...b, villes: b.villes.map((x, k) => (k === i ? { ...x, hebergement: n } : x)) })} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Frais terminaux et taux</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span>Go-Pass intérieur (par trajet)</span>
                      <Cellule valeur={b.fraisTerminaux.gopassINT} edition={edition} onChange={(n) => setContenu({ ...b, fraisTerminaux: { ...b.fraisTerminaux, gopassINT: n } })} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span>Go-Pass extérieur (par trajet)</span>
                      <Cellule valeur={b.fraisTerminaux.gopassEXT} edition={edition} onChange={(n) => setContenu({ ...b, fraisTerminaux: { ...b.fraisTerminaux, gopassEXT: n } })} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span>1 EUR en USD</span>
                      {edition ? <Input type="number" min={0} step="0.0001" className="num h-8 w-24 text-right" value={b.tauxChange.EUR} onChange={(e) => setContenu({ ...b, tauxChange: { EUR: Number(e.target.value) || 0 } })} /> : <span className="num">{b.tauxChange.EUR}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Source : {b.source}. En vigueur du {dateFr(b.debut)} {b.fin ? `au ${dateFr(b.fin)}` : 'jusqu’à la version suivante'}.
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      <Dialog open={creation} onOpenChange={setCreation}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Préparer une nouvelle version</DialogTitle>
            <DialogDescription>Copie de la version {choisie} ; modifiez ensuite les montants puis mettez-la en vigueur à sa date d’effet.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="v">Version (année-numéro)</Label>
              <Input id="v" value={nouvelle.version} onChange={(e) => setNouvelle({ ...nouvelle, version: e.target.value })} placeholder="2027-1" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="d">Date d’effet</Label>
              <Input id="d" type="date" value={nouvelle.debut} onChange={(e) => setNouvelle({ ...nouvelle, debut: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="l">Libellé</Label>
              <Input id="l" value={nouvelle.libelle} onChange={(e) => setNouvelle({ ...nouvelle, libelle: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="s">Source (décision, note de service)</Label>
              <Input id="s" value={nouvelle.source} onChange={(e) => setNouvelle({ ...nouvelle, source: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreation(false)}>Fermer</Button>
            <Button onClick={creer} disabled={!/^\d{4}-\d{1,2}$/.test(nouvelle.version.trim()) || nouvelle.libelle.trim().length < 3 || !nouvelle.debut || actions.creer.isPending}>
              Préparer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        ouvert={vigueur}
        onOpenChange={setVigueur}
        titre={`Mettre en vigueur la version ${choisie ?? ''}`}
        description={`Elle s’appliquera aux TDR soumis à partir de sa date d’effet ; la version actuellement en vigueur sera archivée la veille. Enregistrez d’abord vos modifications.`}
        libelle="Mettre en vigueur"
        onConfirmer={mettreEnVigueur}
        chargement={actions.mettreEnVigueur.isPending}
      />
    </div>
  );
}
